import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BaseChart, Retry } from '@components/common';
import DragHandle from '@components/common/DnD/DragHandle';
import useToolbox from '@components/metrics-panel-hz/hooks/useToolbox';
import { filterNAMetricsAtom } from '@src/atoms/page';
import { getFirstAppliedFilter } from '@store/common/selectors';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import MetricHelpPopup from './Tools/Help';
import Header from './Header';
import RemoveMetric from './RemoveMetric';
import {
  CHART_SIZE_MAPPING,
  panelStyleAtom,
  errorDataAtom,
} from '@src/components/metrics-panel-hz/atoms/index';
import SecondaryMetric from './SecondaryMetric';
import cardStyles from './index.less';
import { DeletedMetricWithBorder } from '@components/common/DeletedMetric';
import { StyledRightTools } from './styled';
import AskNexaButton from './AskNexaButton';

// converted from class componenet
function MetricCard(props) {
  const { metric, isSelected, diagMode } = props;
  const errorData = useAtomValue(errorDataAtom);
  const { chartSize } = useAtomValue(panelStyleAtom);
  const secondaryMetric = useSelector(globalOrderBySelector);

  const classes = classNames(cardStyles.metricCard, {
    active: isSelected,
  });

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: metric });

  const showSecondaryMetric = diagMode && isSelected && secondaryMetric !== metric;

  const dndStyle: any = {
    transform: CSS.Transform.toString(transform),
    transition,
    // primary chart height for diag is same as large mode, secondary adds additional height
    height: diagMode
      ? showSecondaryMetric
        ? CHART_SIZE_MAPPING.d.height
        : CHART_SIZE_MAPPING.l.height
      : chartSize.height,
  };

  // hide all cards except selected card for diagnostics mode
  if (diagMode && !isSelected) {
    dndStyle.display = 'none';
  }

  const isDeletedMetric = !!errorData[metric];

  return (
    <div key={metric} id={metric} className={classes} ref={setNodeRef} style={dndStyle}>
      <DragHandle {...attributes} {...listeners} />
      <StyledRightTools>
        {!isDeletedMetric && <MetricHelpPopup metric={metric} />}
        <RemoveMetric metric={metric} />
      </StyledRightTools>
      {isDeletedMetric ? <DeletedMetricWithBorder /> : <MetricCardChildren {...props} />}
    </div>
  );
}

function MetricCardChildren(props) {
  const { metric, isSelected, activeTool, onSelect, diagMode } = props;
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const metricsToNA = useAtomValue(filterNAMetricsAtom);
  const { chartSize } = useAtomValue(panelStyleAtom);
  const secondaryMetric = useSelector(globalOrderBySelector);
  const chartContainerRef = useRef(null);

  const naMode = metricsToNA.includes(metric);

  const {
    chartMode,
    setChartMode,
    chartInstanceRefCb,
    distributionQueryState,
    instance,
    toggleLegend,
    distribution,
    selectDistribution,
  } = useToolbox({
    metric,
    activeTool,
    appliedFilter,
    naMode,
    diagMode,
    isSelected,
    chartContainerRef,
  });

  const { transform, transition } = useSortable({ id: metric });

  useEffect(() => {
    if (instance && typeof instance.resize === 'function') {
      setTimeout(() => {
        instance.resize();
      }, 150);
    }
  }, [instance, chartSize, isSelected, diagMode]);

  const showSecondaryMetric = diagMode && isSelected && secondaryMetric !== metric;

  const dndStyle: any = {
    transform: CSS.Transform.toString(transform),
    transition,
    // primary chart height for diag is same as large mode, secondary adds additional height
    height: diagMode
      ? showSecondaryMetric
        ? CHART_SIZE_MAPPING.d.height
        : CHART_SIZE_MAPPING.l.height
      : chartSize.height,
  };

  // hide all cards except selected card for diagnostics mode
  if (diagMode && !isSelected) {
    dndStyle.display = 'none';
  }

  const onClick = () => {
    onSelect(metric);
  };

  const chartClass = classNames(cardStyles.bodySection, {
    [cardStyles.naMode]: naMode,
  });

  return (
    <div
      id={`trends-${metric}`}
      className={chartClass}
      tabIndex={-1}
      role="button"
      onClick={onClick}
      ref={chartContainerRef}
    >
      <Header
        {...props}
        chartMode={chartMode}
        setChartMode={setChartMode}
        toggleLegend={toggleLegend}
        distribution={distribution}
        selectDistribution={selectDistribution}
        naMode={naMode}
      />
      <Retry {...distributionQueryState}>
        <BaseChart
          key={metric}
          chartId={metric}
          chartInstanceRefCb={chartInstanceRefCb}
          style={{ height: '100%' }}
        />
      </Retry>
      {showSecondaryMetric && <SecondaryMetric activeTool={activeTool} />}
      <AskNexaButton
        metric={metric}
        chartMode={chartMode}
        isSelected={isSelected}
        diagMode={diagMode}
      />
    </div>
  );
}

export default React.memo(MetricCard);
